import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import DeleteConfirmDialog from '@/components/dialogs/DeleteConfirmDialog';
import type { Position } from '@arrowise/shared';
import { Pencil, Trash2, Calendar, Building, BarChart3 } from 'lucide-react';
import { usePositionsStore } from '@/store/positionsStore';
import { Link, useNavigate } from 'react-router-dom';

export interface PositionListItemProps {
    position: Position;
    canManagePosition?: boolean;
}

export default function PositionListItem({ position, canManagePosition }: PositionListItemProps) {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const { deletePosition } = usePositionsStore();
    const navigate = useNavigate();

    const handleDelete = async () => {
        const success = await deletePosition(position.id);
        if (success) {
            setConfirmOpen(false);
        }
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return null;
        return new Intl.DateTimeFormat(undefined, {
            dateStyle: 'medium'
        }).format(new Date(dateString));
    };

    const hiringEndDate = formatDate(position.hiringEndDate);
    const isExpired = position.hiringEndDate && new Date(position.hiringEndDate) < new Date();

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-4">
                <div className="space-y-1">
                    <CardTitle className="text-base flex items-center gap-2">
                        {position.name}
                        {position.teamId && (
                            <Badge variant="secondary" className="flex items-center gap-1">
                                <Building className="size-3" />
                                Team
                            </Badge>
                        )}
                    </CardTitle>
                    <CardDescription className="space-y-1">
                        {position.description && (
                            <div>{position.description}</div>
                        )}
                        <div className="flex items-center gap-4 text-sm">
                            {hiringEndDate && (
                                <div className="flex items-center gap-1">
                                    <Calendar className="size-3" />
                                    <span className={isExpired ? 'text-destructive' : ''}>
                                        Hiring ends: {hiringEndDate}
                                    </span>
                                    {isExpired && <Badge variant="destructive" className="text-xs">Expired</Badge>}
                                </div>
                            )}
                            <div className="text-muted-foreground">
                                Created {formatDate(position.createdAt)}
                            </div>
                        </div>
                    </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/app/positions/${position.id}/compare`)}
                    >
                        <BarChart3 className="size-4 mr-1" /> Compare candidates
                    </Button>
                    {canManagePosition && (
                        <>
                            <Link to={`/app/positions/${position.id}/edit`}>
                                <Button size="sm" variant="outline">
                                    <Pencil className="size-4" />
                                </Button>
                            </Link>
                            <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => setConfirmOpen(true)}
                            >
                                <Trash2 className="size-4" />
                            </Button>
                            <DeleteConfirmDialog
                                open={confirmOpen}
                                onOpenChange={setConfirmOpen}
                                title="Delete position?"
                                description={`This action cannot be undone. This will permanently delete the position "${position.name}" and all associated sessions.`}
                                onConfirm={handleDelete}
                                confirmLabel="Delete"
                                cancelLabel="Cancel"
                                confirmVariant="destructive"
                            />
                        </>
                    )}
                </div>
            </CardHeader>
        </Card>
    );
}
